<template>
  <detail
    :on-manager="onManager"
    :data="data"
    :base-info="baseInfo"
    :extra-info="extraInfo"
    :hidden-keys="['status']"
    :full-width="true" />
</template>

<script>
import { parseTemplate } from '@Reports/sections/ReportCreate/utils'
import ReportTemplate from '@Reports/sections/ReportCreate/components/ReportTemplate'
import WindowsMixin from '@/mixins/windows'
import { getEnabledTableColumn, getApplicationScopeTableColumn } from '@/utils/common/tableColumn'
import CustomReportViewer from '@Reports/views/custom-report/template-viewer'
import ColumnsMixin from '../mixins/columns'

export default {
  name: 'MonthReportDetail',
  components: {
    // eslint-disable-next-line vue/no-unused-components
    CustomReportViewer,
  },
  mixins: [WindowsMixin, ColumnsMixin],
  props: {
    data: {
      type: Object,
      required: true,
    },
    onManager: {
      type: Function,
      required: true,
    },
    wholeType: String,
  },
  data () {
    return {
      recipientList: [],
      extraInfo: [
        {
          title: this.$t('report.email_info'),
          items: [
            {
              field: 'subject',
              title: this.wholeType === 'daily' ? this.$t('report.daily_report_title') : this.$t('report.month_report_title'),
              formatter: ({ row }) => {
                if (!row.id.startsWith('default_')) {
                  return row.subject
                }
                const { title_prefix, title } = parseTemplate(row)
                return `${title_prefix} ${title}`
              },
            },
            {
              field: 'template',
              title: this.wholeType === 'daily' ? this.$t('report.daily_report_template') : this.$t('report.month_report_template'),
              slots: {
                default: ({ row }) => {
                  if (!row.id.startsWith('default_')) {
                    return [
                      <CustomReportViewer
                        reportData={row}
                        type={this.wholeType}
                        templateList={row.templates || []}
                        isTemplateEdit={false}
                        isPage={false}
                        hideEmpty={row.hasOwnProperty('hide_empty') ? row.hide_empty : true} />,
                    ]
                  }
                  const { template_header, template_footer, report_type } = parseTemplate(row)
                  return [
                    <div style="border: solid 1px #d9d9d9;margin-top:5px">
                      <div style="padding:15px">{template_header}</div>
                      <ReportTemplate type={report_type} inSidepage={true} wholeType={this.wholeType} />
                      <div style="padding:15px" domPropsInnerHTML={template_footer}></div>
                    </div>,
                  ]
                },
              },
            },
          ],
        },
      ],
    }
  },
  computed: {
    baseInfo () {
      const list = [
        getEnabledTableColumn(),
        {
          field: 'role',
          title: this.$t('report.notification_role'),
          slots: {
            default: ({ row }) => {
              return <list-body-cell-wrap row={row} field="role" customEdit customEditCallback={this.editRole} />
            },
          },
        },
        {
          field: 'report_type',
          title: this.wholeType === 'daily' ? this.$t('report.daily_report_type') : this.$t('report.month_report_type'),
          formatter: ({ row }) => {
            if (row.id.includes('system')) return this.$t('report.month_report_type.system')
            if (row.id.includes('domain')) return this.$t('report.month_report_type.domain')
            if (row.id.includes('project')) return this.$t('report.month_report_type.project')
            if (row.templates || row.css || row.type) return this.$t('report.monitor_daily_report')
            return '-'
          },
        },
        {
          field: 'frequency',
          title: this.$t('report.alert_frequency'),
          formatter: ({ row }) => {
            const { day, hour, minute } = row
            return this.$t('report.month_report_frequency', [day, String(hour).padStart(2, '0'), String(minute).padStart(2, '0')])
          },
        },
        getApplicationScopeTableColumn({
          vm: this,
          resource: `${this.wholeType}_reports`,
          scope: 'domain',
        }),
        {
          field: 'hide_empty',
          title: h => [
            <span>{this.$t('report.report_hide_empty')}</span>,
            <a-tooltip class="ml-1" title={this.$t('report.report_hide_empty_tip')}>
              <a-icon type="question-circle" style="color: #aaa;" />
            </a-tooltip>,
          ],
          formatter: ({ row }) => {
            const hideEmpty = row.hasOwnProperty('hide_empty') ? row.hide_empty : true
            return hideEmpty ? this.$t('table.title.on') : this.$t('table.title.off')
          },
        },
      ]
      if (this.wholeType === 'daily' && (this.data.templates || this.data.css || this.data.type)) {
        list.push({
          field: 'recipients',
          title: this.$t('compute.text_740'),
          slots: {
            default: ({ row }, h) => {
              if (!row.user_ids || !row.user_ids.length) return '-'
              return this.recipientList.map(val => {
                return h('a-tag', val.name)
              })
            },
          },
        })
      }
      return list
    },
  },
  created () {
    this.getRecipients()
  },
  methods: {
    async getRecipients () {
      try {
        if (!this.data.user_ids || !this.data.user_ids.length) return
        const { data: { data: recipientList } } = await new this.$Manager('receivers', 'v1')
          .list({
            params: {
              scope: this.$store.getters.scope,
              with_meta: true,
              project_domain_filter: true,
              filter: `id.in(${this.data.user_ids.map(item => `'${item}'`).join(',')})`,
            },
          })
        this.recipientList = recipientList
      } catch (error) {
        console.error(error)
        return []
      }
    },
    editRole () {
      this.createDialog('ReportChangeNotifyRoleDialog', {
        name: this.$t(`report.${this.wholeType}_report`),
        wholeType: this.wholeType,
        data: [this.data],
        onManager: this.onManager,
        columns: this.columns,
      })
    },
  },
}
</script>
